import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Avatar,
  Slider,
  Stack,
  Autocomplete,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Movie as MovieIcon,
  Person as PersonIcon,
  Description as ScriptIcon,
  PlayArrow as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Group as GroupIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  AutoAwesome as AutoAwesomeIcon,
  MenuBook as ActIcon,
  Theaters as SceneIcon,
  RateReview as ReviewIcon,
  FormatAlignLeft as FormatIcon,
  Chat as DialogueIcon,
  Visibility as PreviewIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import {
  ScreenplaySession,
  StartScreenplaySessionRequest,
  ScreenplayCharacter,
  ScreenplayAct,
  ScreenplayScene,
  ScreenplayFormat,
  ScreenplayGenre,
  TimeOfDay,
} from '../models/screenplay';
import { ScreenplayService } from '../services/screenplayService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { SessionWithAssets } from '../services/assetRetrievalService';

// TabPanel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

// Utility function to format screenplay content with proper styling
const formatScreenplayContent = (content: string): React.ReactNode => {
  if (!content) return '';
  
  // Split content into lines for processing
  const lines = content.split('\n');
  
  return lines.map((line, index) => {
    const trimmedLine = line.trim();
    
    // Scene heading (INT./EXT.)
    if (trimmedLine.startsWith('INT.') || trimmedLine.startsWith('EXT.')) {
      return (
        <span key={index} style={{ 
          display: 'block', 
          fontWeight: 'bold', 
          color: '#4fc3f7',
          marginBottom: '8px',
          textTransform: 'uppercase'
        }}>
          {line}
        </span>
      );
    }
    
    // Character name (all caps, typically centered, not too long)
    if (trimmedLine.length > 0 && 
        trimmedLine.length < 50 && 
        trimmedLine === trimmedLine.toUpperCase() && 
        !trimmedLine.includes('.') &&
        /^[A-Z][A-Z\s]+(\([A-Z\s.]+\))?$/.test(trimmedLine)) {
      return (
        <span key={index} style={{ 
          display: 'block', 
          textAlign: 'center',
          fontWeight: 'bold',
          color: '#81c784',
          marginTop: '12px',
          marginBottom: '4px'
        }}>
          {line}
        </span>
      );
    }
    
    // Parentheticals (in parentheses)
    if (trimmedLine.startsWith('(') && trimmedLine.endsWith(')')) {
      return (
        <span key={index} style={{ 
          display: 'block',
          textAlign: 'center',
          fontStyle: 'italic',
          color: '#ffb74d',
          fontSize: '0.85rem',
          marginBottom: '4px'
        }}>
          {line}
        </span>
      );
    }
    
    // Regular line (action or dialogue)
    return (
      <span key={index} style={{ display: 'block' }}>
        {line || '\u00A0'}
      </span>
    );
  });
};

// Utility function to format character text for better readability
const formatCharacterText = (text: string): string => {
  if (!text) return '';
  
  return text
    // Clean up markdown formatting but preserve structure
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown but keep content
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic markdown but keep content
    // Clean up excessive whitespace while preserving intentional breaks
    .replace(/\n\s*\n\s*\n+/g, '\n\n') // Reduce multiple newlines to double newlines
    .replace(/ {2,}/g, ' ') // Replace multiple spaces with single space (but preserve intentional indentation)
    .replace(/\n +/g, '\n') // Remove leading spaces after newlines
    // Format bullet points nicely
    .replace(/\n\s*\*\s+/g, '\n• ') // Convert asterisk bullets to proper bullets
    .replace(/\n\s*-\s+/g, '\n• ') // Convert dash bullets to proper bullets
    // Clean up section headers (like "CHARACTER NAME:" etc.)
    .replace(/([A-Z\s]+:)\s*/g, '\n$1 ') // Add proper spacing around section headers
    // Remove redundant character name repetition at the beginning
    .replace(/^.*I can help you develop.*?Here's.*?:\s*/i, '') // Remove the intro text
    .replace(/^\*\*CHARACTER NAME:\*\* [A-Z]+\s*/i, '') // Remove redundant character name section
    .trim();
};

const ScreenplayGenerator: React.FC = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Main state
  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<ScreenplaySession[]>([]);
  const [currentSession, setCurrentSession] = useState<ScreenplaySession | null>(null);
  
  // Sub-tabs for current screenplay
  const [screenplayTab, setScreenplayTab] = useState(0);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // New session form states
  const [sessionForm, setSessionForm] = useState({
    title: '',
    concept: '',
    genre: 'Drama' as ScreenplayGenre,
    format: 'Feature Film' as ScreenplayFormat,
    actCount: 3,
    generateTreatment: true,
    temperature: 0.7
  });
  
  // Character form states
  const [characterForm, setCharacterForm] = useState({
    name: '',
    description: '',
    role: '',
    backstory: '',
    expandCharacter: true,
  });
  
  // Scene form states
  const [sceneForm, setSceneForm] = useState({
    actNumber: 1,
    sceneDescription: '',
    location: '',
    timeOfDay: 'DAY' as TimeOfDay,
    charactersInScene: [] as string[],
    generateFormatted: true,
  });

  // UI state
  const [selectedAct, setSelectedAct] = useState<number | null>(null);
  const [generatedScript, setGeneratedScript] = useState<string>('');
  const [showCharacterDialog, setShowCharacterDialog] = useState(false);
  const [showSceneDialog, setShowSceneDialog] = useState(false);

  // Asset retrieval hook for backend data
  const { sessions: backendSessions, loading: backendLoading, refetch: refetchBackendData } = useAssetRetrieval({
    agentType: 'ScreenplaySession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true
  });

  // Merge backend sessions with local sessions
  useEffect(() => {
    const loadedSessions = ScreenplayService.loadSessions();

    // Convert backend sessions to ScreenplaySession format
    const convertedBackendSessions: ScreenplaySession[] = backendSessions.map((backendSession: SessionWithAssets) => {
      // Parse sessionData JSON string to extract screenplay data
      let sessionData: any = {};
      try {
        if (backendSession.session.sessionData) {
          sessionData = JSON.parse(backendSession.session.sessionData);
        }
      } catch (e) {
        console.error('[ScreenplayGenerator] Failed to parse sessionData:', e);
      }

      // Normalize Acts from backend (Pascal case) to component format (camelCase)
      const normalizedActs = (sessionData.Acts || backendSession.session.metadata?.acts || []).map((act: any) => ({
        actNumber: act.ActNumber || act.actNumber,
        title: act.Title || act.title || `Act ${act.ActNumber || act.actNumber}`,
        description: act.Description || act.description,
        scenes: act.Scenes || act.scenes || [],
        status: act.Status || act.status || 'Planned',
        createdAt: act.CreatedAt ? new Date(act.CreatedAt) : new Date()
      }));

      // Normalize Characters from backend
      const normalizedCharacters = (sessionData.Characters || backendSession.session.metadata?.characters || []).map((char: any) => ({
        name: char.Name || char.name,
        description: char.Description || char.description,
        role: char.Role || char.role
      }));

      return {
        id: backendSession.session.id,
        title: sessionData.Title || backendSession.session.title || backendSession.session.metadata?.title || 'Untitled Screenplay',
        concept: sessionData.Concept || backendSession.session.concept || backendSession.session.metadata?.concept || '',
        genre: (sessionData.Genre || backendSession.session.genre || backendSession.session.metadata?.genre || 'Drama') as ScreenplayGenre,
        format: (sessionData.Format || backendSession.session.metadata?.format || 'Feature Film') as ScreenplayFormat,
        treatment: sessionData.Treatment || backendSession.assets.find(a => a.assetType === 'treatment')?.textContent,
        characters: normalizedCharacters,
        acts: normalizedActs,
        scriptContent: sessionData.GeneratedScript || backendSession.assets.find(a => a.assetType === 'script')?.textContent,
        createdAt: new Date(backendSession.session.createdAt),
        lastUpdatedAt: new Date(backendSession.session.lastUpdatedAt || backendSession.session.updatedAt || backendSession.session.createdAt)
      };
    });

    // Merge: backend sessions + local-only sessions (filter out duplicates by id)
    const backendIds = new Set(convertedBackendSessions.map(s => s.id));
    const uniqueLocalSessions = loadedSessions.filter(s => !backendIds.has(s.id));
    const merged = [...convertedBackendSessions, ...uniqueLocalSessions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setSessions(merged);
    if (merged.length > 0 && !currentSession) {
      setCurrentSession(merged[0]);
    }
  }, [backendSessions]);

  const loadSessions = () => {
    refetchBackendData();
  };

  const validateSessionForm = (): string | null => {
    if (!sessionForm.title.trim()) return 'Title is required';
    if (!sessionForm.concept.trim()) return 'Concept is required';
    if (sessionForm.actCount < 1 || sessionForm.actCount > 5) return 'Act count must be between 1 and 5';
    return null;
  };

  const handleStartNewSession = async () => {
    const validation = validateSessionForm();
    if (validation) {
      setError(validation);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: StartScreenplaySessionRequest = {
        concept: sessionForm.concept,
        title: sessionForm.title || undefined,
        genre: sessionForm.genre,
        format: sessionForm.format,
        actCount: sessionForm.actCount,
        generateTreatment: sessionForm.generateTreatment,
      };

      const response = await ScreenplayService.startSession(request);
      
      // Fetch the full session data
      const fullSession = await ScreenplayService.getSessionStatus(response.sessionId);
      
      // Convert to local session format
      const newSession: ScreenplaySession = {
        id: fullSession.sessionId,
        title: fullSession.title || sessionForm.title || 'Untitled Screenplay',
        concept: fullSession.concept || sessionForm.concept,
        genre: fullSession.genre,
        format: fullSession.format || 'Feature Film',
        treatment: fullSession.treatment,
        characters: (fullSession.characters || []).map(c => ({
          name: c.name,
          description: c.description,
          role: c.role,
          createdAt: new Date()
        })),
        acts: (fullSession.acts || []).map((a: any) => ({
          actNumber: a.actNumber || a.ActNumber,
          title: a.title || a.Title || `Act ${a.actNumber || a.ActNumber}`,
          description: a.description || a.Description,
          scenes: (a.scenes || a.Scenes || []).map((scene: any) => ({
            ...scene,
            createdAt: new Date()
          })),
          status: (a.status || a.Status || 'Draft') as 'Planned' | 'In Progress' | 'Complete',
          createdAt: new Date()
        })),
        scriptContent: fullSession.scriptContent,
        createdAt: new Date(),
        lastUpdatedAt: new Date()
      };

      ScreenplayService.saveSession(newSession);
      setCurrentSession(newSession);
      setSessions([newSession, ...sessions]);
      await refetchBackendData(); // Refresh backend data
      
      // Clear form and switch to current session tab
      setSessionForm({
        title: '',
        concept: '',
        genre: 'Drama',
        format: 'Feature Film',
        actCount: 3,
        generateTreatment: true,
        temperature: 0.7
      });
      setTabValue(2);
    } catch (err: any) {
      setError(err.message || 'Failed to start screenplay session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCharacter = async () => {
    if (!currentSession) return;
    if (!characterForm.name.trim()) {
      setError('Character name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ScreenplayService.addCharacter(currentSession.id, {
        name: characterForm.name,
        description: characterForm.description,
        role: characterForm.role,
        backstory: characterForm.backstory,
        expandCharacter: characterForm.expandCharacter
      });

      const newCharacter: ScreenplayCharacter = {
        name: response.characterName || characterForm.name,
        description: response.characterDescription || response.expandedDescription || characterForm.description,
        role: response.role || characterForm.role,
        backstory: response.backstory || response.expandedBackstory || characterForm.backstory,
        createdAt: new Date()
      };

      const updatedSession = {
        ...currentSession,
        characters: [...currentSession.characters, newCharacter],
        lastUpdatedAt: new Date()
      };

      ScreenplayService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
      setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
      
      // Clear form
      setCharacterForm({
        name: '',
        description: '',
        role: '',
        backstory: '',
        expandCharacter: true,
      });
      setShowCharacterDialog(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add character');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddScene = async () => {
    if (!currentSession) return;
    if (!sceneForm.sceneDescription.trim()) {
      setError('Scene description is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Adding scene with data:', {
        sessionId: currentSession.id,
        actNumber: sceneForm.actNumber,
        request: {
          sceneDescription: sceneForm.sceneDescription,
          location: sceneForm.location,
          timeOfDay: sceneForm.timeOfDay,
          charactersInScene: sceneForm.charactersInScene,
          generateFormattedScene: sceneForm.generateFormatted
        }
      });
      
      const response = await ScreenplayService.addScene(currentSession.id, sceneForm.actNumber, {
        sceneDescription: sceneForm.sceneDescription,
        location: sceneForm.location,
        timeOfDay: sceneForm.timeOfDay,
        charactersInScene: sceneForm.charactersInScene,
        generateFormattedScene: sceneForm.generateFormatted
      });

      // Extract the formatted content and remove markdown code blocks
      let formattedContent = response.formattedContent || response.formattedScene || '';
      if (formattedContent.startsWith('```')) {
        // Remove markdown code block wrapper
        formattedContent = formattedContent
          .replace(/^```\w*\n?/, '') // Remove opening code block
          .replace(/\n?```$/, '');    // Remove closing code block
      }
      
      const newScene: ScreenplayScene = {
        sceneNumber: response.sceneNumber,
        location: response.location || sceneForm.location || 'LOCATION',
        timeOfDay: response.timeOfDay || sceneForm.timeOfDay,
        description: response.sceneDescription || sceneForm.sceneDescription,
        formattedContent: formattedContent, // Store the cleaned content
        heading: response.heading || ScreenplayService.formatSceneHeading(response.location || sceneForm.location, response.timeOfDay || sceneForm.timeOfDay),
        createdAt: new Date()
      };

      const updatedActs = (currentSession.acts || []).map((act: any) => {
        if (act.actNumber === sceneForm.actNumber) {
          return {
            ...act,
            scenes: [...(act.scenes || act.Scenes || []), newScene]
          };
        }
        return act;
      });

      const updatedSession = {
        ...currentSession,
        acts: updatedActs,
        lastUpdatedAt: new Date()
      };

      ScreenplayService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
      setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
      
      // Clear form
      setSceneForm({
        actNumber: 1,
        sceneDescription: '',
        location: '',
        timeOfDay: 'DAY',
        charactersInScene: [],
        generateFormatted: true,
      });
      setShowSceneDialog(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add scene');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    ScreenplayService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    loadSessions();
  };

  const exportSession = () => {
    if (currentSession) {
      ScreenplayService.exportSession(currentSession);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const calculateProgress = () => {
    if (!currentSession) return { total: 0, characters: 0, acts: 0, scenes: 0 };

    const characterProgress = Math.min(100, (currentSession.characters?.length || 0) * 20);
    const acts = currentSession.acts || [];
    const actsWithScenes = acts.filter((a: any) => {
      const sceneCount = a.scenes?.length || a.Scenes?.length || 0;
      return sceneCount > 0;
    });
    const actProgress = acts.length > 0 ? (actsWithScenes.length / acts.length) * 100 : 0;
    const totalScenes = acts.reduce((sum: number, act: any) => sum + (act.scenes?.length || act.Scenes?.length || 0), 0);
    const sceneProgress = acts.length > 0 ? Math.min(100, (totalScenes / (acts.length * 5)) * 100) : 0;
    const totalProgress = Math.round((characterProgress + actProgress + sceneProgress) / 3);

    return {
      total: totalProgress,
      characters: characterProgress,
      acts: actProgress,
      scenes: sceneProgress
    };
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <MovieIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Screenplay Studio' : 'Screenplay Generator Studio'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'AI-powered screenplay creation' : 'Create professional screenplays with AI-powered assistance'}
        </Typography>
      </Box>

      {/* Main Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 48
            }
          }}
        >
          <Tab label="Sessions" />
          <Tab label={isMobile ? 'New' : 'Create New'} />
          {currentSession && <Tab label={isMobile ? 'Current' : 'Current Screenplay'} />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              No screenplays found
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setTabValue(1)}
              size="large"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {isMobile ? 'Create Screenplay' : 'Create Your First Screenplay'}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {sessions.map((session) => (
              <Grid item xs={12} md={6} key={session.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: currentSession?.id === session.id ? 2 : 1,
                    borderColor: currentSession?.id === session.id ? 'primary.main' : 'divider'
                  }}
                  onClick={() => {
                    setCurrentSession(session);
                    setTabValue(2);
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography 
                        variant="h6"
                        sx={{ 
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                          flex: 1,
                          mr: 1
                        }}
                      >
                        {session.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        color="error"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={session.genre || 'No genre'} 
                        size="small" 
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                      <Chip 
                        label={session.format} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {session.concept.substring(0, isMobile ? 60 : 100)}...
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {isMobile ? new Date(session.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Created: ${new Date(session.createdAt).toLocaleDateString()}`} •
                      {session.characters?.length || 0} {isMobile ? 'chars' : 'characters'} •
                      {session.acts?.reduce((sum: number, act: any) => sum + (act.scenes?.length || act.Scenes?.length || 0), 0) || 0} scenes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Create New Screenplay */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {isMobile ? 'New Screenplay' : 'Create New Screenplay'}
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Screenplay Title *"
                value={sessionForm.title}
                onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                placeholder={isMobile ? "e.g., The Last Adventure" : "e.g., The Last Adventure"}
                error={!sessionForm.title.trim()}
                helperText={!sessionForm.title.trim() ? "Title is required" : ""}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label="Concept *"
                value={sessionForm.concept}
                onChange={(e) => setSessionForm({ ...sessionForm, concept: e.target.value })}
                placeholder={isMobile ? "Describe your concept..." : "Describe your screenplay concept, plot, and main themes..."}
                error={!sessionForm.concept.trim()}
                helperText={!sessionForm.concept.trim() ? "Concept is required" : (isMobile ? "Main story idea" : "The main idea and story of your screenplay")}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Genre</InputLabel>
                <Select
                  value={sessionForm.genre}
                  onChange={(e) => setSessionForm({ ...sessionForm, genre: e.target.value as ScreenplayGenre })}
                  label="Genre"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="Drama" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Drama</MenuItem>
                  <MenuItem value="Comedy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Comedy</MenuItem>
                  <MenuItem value="Action" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Action</MenuItem>
                  <MenuItem value="Thriller" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Thriller</MenuItem>
                  <MenuItem value="Horror" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Horror</MenuItem>
                  <MenuItem value="Romance" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Romance</MenuItem>
                  <MenuItem value="Sci-Fi" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sci-Fi</MenuItem>
                  <MenuItem value="Fantasy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Fantasy</MenuItem>
                  <MenuItem value="Mystery" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Mystery</MenuItem>
                  <MenuItem value="Documentary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Documentary</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Format</InputLabel>
                <Select
                  value={sessionForm.format}
                  onChange={(e) => setSessionForm({ ...sessionForm, format: e.target.value as ScreenplayFormat })}
                  label="Format"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="Feature Film" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Feature' : 'Feature Film'}
                  </MenuItem>
                  <MenuItem value="Short Film" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Short' : 'Short Film'}
                  </MenuItem>
                  <MenuItem value="TV Pilot" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>TV Pilot</MenuItem>
                  <MenuItem value="Web Series" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Web' : 'Web Series'}
                  </MenuItem>
                  <MenuItem value="Stage Play" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Stage' : 'Stage Play'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={isMobile ? 'Acts' : 'Number of Acts'}
                value={sessionForm.actCount}
                onChange={(e) => setSessionForm({ ...sessionForm, actCount: parseInt(e.target.value) || 3 })}
                InputProps={{ 
                  inputProps: { min: 1, max: 5 },
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                helperText={isMobile ? "Typically 3" : "Typically 3 for feature films"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sessionForm.generateTreatment}
                    onChange={(e) => setSessionForm({ ...sessionForm, generateTreatment: e.target.checked })}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Generate Treatment
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ px: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="body2" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {isMobile ? 'Creativity: ' : 'AI Creativity Level: '}{sessionForm.temperature}
                </Typography>
                <Slider
                  value={sessionForm.temperature}
                  onChange={(_, value) => setSessionForm({ ...sessionForm, temperature: value as number })}
                  min={0}
                  max={1}
                  step={0.1}
                  marks={[
                    { value: 0, label: isMobile ? 'Cons.' : 'Conservative' },
                    { value: 0.5, label: 'Balanced' },
                    { value: 1, label: 'Creative' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ 
                    mx: { xs: 1, sm: 2 },
                    '& .MuiSlider-markLabel': {
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleStartNewSession}
            disabled={isLoading || validateSessionForm() !== null}
            startIcon={isLoading ? <CircularProgress size={20} /> : (!isMobile && <AddIcon />)}
            sx={{ 
              mt: { xs: 2, sm: 3 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44
            }}
          >
            {isLoading 
              ? (isMobile ? 'Creating...' : 'Creating Screenplay...') 
              : (isMobile ? 'Create' : 'Create Screenplay')
            }
          </Button>
        </Paper>
      </TabPanel>

      {currentSession && (
        <TabPanel value={tabValue} index={2}>
          {/* Current Screenplay Sub-tabs */}
          <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
            <Tabs 
              value={screenplayTab} 
              onChange={(_, newValue) => setScreenplayTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  minHeight: 48
                }
              }}
            >
              <Tab label="Overview" />
              <Tab label={isMobile ? 'Characters' : 'Characters'} />
              <Tab label={isMobile ? 'Acts' : 'Acts & Scenes'} />
              <Tab label="Treatment" />
              <Tab label="Script" />
            </Tabs>
          </Paper>

          <TabPanel value={screenplayTab} index={0}>
            {/* Overview Tab */}
            <Box>
              {/* Progress Card */}
              <Card sx={{ mb: { xs: 2, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Progress' : 'Project Progress'}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {isMobile ? 'Overall' : 'Overall Progress'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={calculateProgress().total} 
                      sx={{ height: { xs: 8, sm: 10 }, borderRadius: 5, my: 1 }} 
                    />
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {calculateProgress().total}%
                    </Typography>
                  </Box>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={4}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        Characters
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={calculateProgress().characters}
                        sx={{ height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        Acts
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={calculateProgress().acts}
                        sx={{ height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        Scenes
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={calculateProgress().scenes}
                        sx={{ height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Project Details */}
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Details' : 'Project Details'}
                      </Typography>
                      <Box sx={{ display: 'grid', gap: { xs: 1.5, sm: 2 } }}>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Title
                          </Typography>
                          <Typography 
                            variant="h5"
                            sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem' } }}
                          >
                            {currentSession.title}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}
                          >
                            Genre
                          </Typography>
                          <Chip 
                            label={currentSession.genre || 'Not specified'}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}
                          >
                            Format
                          </Typography>
                          <Chip 
                            label={currentSession.format} 
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Concept
                          </Typography>
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {currentSession.concept}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Button 
                        size="small" 
                        startIcon={!isMobile && <DownloadIcon />} 
                        onClick={exportSession}
                        sx={{ 
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          minHeight: 44
                        }}
                      >
                        Export
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Stats' : 'Quick Stats'}
                      </Typography>
                      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={6}>
                          <Box textAlign="center">
                            <Avatar sx={{ 
                              bgcolor: 'primary.main', 
                              mx: 'auto', 
                              mb: 1,
                              width: { xs: 40, sm: 48 },
                              height: { xs: 40, sm: 48 }
                            }}>
                              <GroupIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </Avatar>
                            <Typography 
                              variant="h4"
                              sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                            >
                              {currentSession.characters.length}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              Characters
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box textAlign="center">
                            <Avatar sx={{ 
                              bgcolor: 'secondary.main', 
                              mx: 'auto', 
                              mb: 1,
                              width: { xs: 40, sm: 48 },
                              height: { xs: 40, sm: 48 }
                            }}>
                              <SceneIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </Avatar>
                            <Typography
                              variant="h4"
                              sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                            >
                              {currentSession.acts?.reduce((sum: number, act: any) => sum + (act.scenes?.length || act.Scenes?.length || 0), 0) || 0}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              Scenes
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          <TabPanel value={screenplayTab} index={1}>
            {/* Characters Tab */}
            <Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                mb: { xs: 2, sm: 3 }
              }}>
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  Characters
                </Typography>
                <Button
                  variant="contained"
                  startIcon={!isMobile && <AddIcon />}
                  onClick={() => setShowCharacterDialog(true)}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44
                  }}
                  fullWidth={isMobile}
                >
                  {isMobile ? 'Add Character' : 'Add Character'}
                </Button>
              </Box>

              {currentSession.characters.length === 0 ? (
                <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                  <PersonIcon sx={{ 
                    fontSize: { xs: 48, sm: 60 }, 
                    color: 'text.secondary', 
                    mb: 2 
                  }} />
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    No Characters Yet
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {isMobile ? 'Add characters to start' : 'Add characters to bring your screenplay to life'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={!isMobile && <AddIcon />}
                    onClick={() => setShowCharacterDialog(true)}
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44
                    }}
                  >
                    {isMobile ? 'Add Character' : 'Add First Character'}
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  {currentSession.characters.map((character, index) => (
                    <Grid item xs={12} key={index}>
                      <Card>
                        <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: { xs: 1, sm: 0 }
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="h6"
                                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                              >
                                {character.name}
                              </Typography>
                              {character.role && (
                                <Chip 
                                  label={character.role} 
                                  size="small" 
                                  sx={{ 
                                    mt: 1,
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                  }} 
                                />
                              )}
                            </Box>
                            <Avatar sx={{ 
                              bgcolor: 'primary.main',
                              width: { xs: 40, sm: 48 },
                              height: { xs: 40, sm: 48 }
                            }}>
                              <PersonIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </Avatar>
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mt: 2, 
                              whiteSpace: 'pre-wrap',
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                          >
                            {formatCharacterText(character.description)}
                          </Typography>
                          {character.backstory && (
                            <Box sx={{ mt: 2 }}>
                              <Typography 
                                variant="subtitle2" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                              >
                                Backstory
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                              >
                                {formatCharacterText(character.backstory)}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Add Character Dialog (simplified inline) */}
              {showCharacterDialog && (
                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'New Character' : 'Add New Character'}
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={isMobile ? 'Name *' : 'Character Name *'}
                        value={characterForm.name}
                        onChange={(e) => setCharacterForm({ ...characterForm, name: e.target.value })}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Role"
                        value={characterForm.role}
                        onChange={(e) => setCharacterForm({ ...characterForm, role: e.target.value })}
                        placeholder={isMobile ? "Protagonist..." : "e.g., Protagonist, Antagonist, Supporting"}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 2}
                        label="Description"
                        value={characterForm.description}
                        onChange={(e) => setCharacterForm({ ...characterForm, description: e.target.value })}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 2}
                        label="Backstory"
                        value={characterForm.backstory}
                        onChange={(e) => setCharacterForm({ ...characterForm, backstory: e.target.value })}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1.5, sm: 2 }
                      }}>
                        <Button
                          variant="contained"
                          onClick={handleAddCharacter}
                          disabled={isLoading || !characterForm.name.trim()}
                          startIcon={isLoading ? <CircularProgress size={20} /> : (!isMobile && <AddIcon />)}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44
                          }}
                          fullWidth={isMobile}
                        >
                          {isMobile ? 'Add' : 'Add Character'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setShowCharacterDialog(false)}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44
                          }}
                          fullWidth={isMobile}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={screenplayTab} index={2}>
            {/* Acts & Scenes Tab */}
            <Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                mb: { xs: 2, sm: 3 }
              }}>
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {isMobile ? 'Acts & Scenes' : 'Acts & Scenes'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={!isMobile && <AddIcon />}
                  onClick={() => setShowSceneDialog(true)}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44
                  }}
                  fullWidth={isMobile}
                >
                  {isMobile ? 'Add Scene' : 'Add Scene'}
                </Button>
              </Box>

              {(currentSession.acts || []).map((act: any) => (
                <Accordion
                  key={act.actNumber}
                  expanded={selectedAct === act.actNumber}
                  onChange={() => setSelectedAct(selectedAct === act.actNumber ? null : act.actNumber)}
                  sx={{ mb: { xs: 1, sm: 2 } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        my: { xs: 1, sm: 1.5 }
                      }
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 0.5, sm: 2 },
                      width: '100%'
                    }}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {act.title}
                      </Typography>
                      <Chip
                        label={`${act.scenes?.length || act.Scenes?.length || 0} ${isMobile ? '' : 'scenes'}`}
                        size="small"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: { xs: 2, sm: 2 } }}>
                    {act.description && (
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Typography 
                          variant="subtitle2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                        >
                          Description
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {act.description}
                        </Typography>
                      </Box>
                    )}
                    {act.scenes.length > 0 ? (
                      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        {act.scenes.map((scene: any) => (
                          <Grid item xs={12} key={scene.sceneNumber}>
                            <Card>
                              <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight="bold"
                                  sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}
                                >
                                  Scene {scene.sceneNumber}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap',
                                  gap: { xs: 0.5, sm: 1 }, 
                                  my: 1 
                                }}>
                                  <Chip 
                                    icon={<LocationIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />} 
                                    label={scene.location} 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                  />
                                  <Chip 
                                    icon={<TimeIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />} 
                                    label={scene.timeOfDay} 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                  />
                                </Box>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    mb: 1,
                                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                                  }}
                                >
                                  {scene.description}
                                </Typography>
                                {scene.formattedContent && (
                                  <Paper 
                                    elevation={2} 
                                    sx={{ 
                                      p: { xs: 2, sm: 3 }, 
                                      bgcolor: '#1a1a1a', 
                                      mb: 2,
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: 'grey.800'
                                    }}
                                  >
                                    <Box 
                                      sx={{ 
                                        fontFamily: '"Courier New", Courier, monospace',
                                        fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                        lineHeight: 1.8,
                                        color: '#f0f0f0',
                                        letterSpacing: '0.02em',
                                        overflowX: 'auto'
                                      }}
                                    >
                                      {formatScreenplayContent(scene.formattedContent)}
                                    </Box>
                                  </Paper>
                                )}
                                {scene.characters && scene.characters.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      Characters:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                      {scene.characters.map((char: any, i: number) => (
                                        <Chip 
                                          key={i} 
                                          label={char} 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          textAlign: 'center',
                          py: 2
                        }}
                      >
                        No scenes in this act yet
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}

              {/* Add Scene Dialog (simplified inline) */}
              {showSceneDialog && (
                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'New Scene' : 'Add New Scene'}
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Act
                        </InputLabel>
                        <Select
                          value={sceneForm.actNumber}
                          onChange={(e) => setSceneForm({ ...sceneForm, actNumber: e.target.value as number })}
                          label="Act"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {(currentSession.acts || []).map(act => (
                            <MenuItem 
                              key={act.actNumber} 
                              value={act.actNumber}
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              {act.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {isMobile ? 'Time' : 'Time of Day'}
                        </InputLabel>
                        <Select
                          value={sceneForm.timeOfDay}
                          onChange={(e) => setSceneForm({ ...sceneForm, timeOfDay: e.target.value as TimeOfDay })}
                          label={isMobile ? 'Time' : 'Time of Day'}
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          <MenuItem value="DAY" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Day</MenuItem>
                          <MenuItem value="NIGHT" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Night</MenuItem>
                          <MenuItem value="DAWN" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Dawn</MenuItem>
                          <MenuItem value="DUSK" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Dusk</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Location"
                        value={sceneForm.location}
                        onChange={(e) => setSceneForm({ ...sceneForm, location: e.target.value })}
                        placeholder={isMobile ? "INT. COFFEE SHOP" : "e.g., INT. COFFEE SHOP"}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 3}
                        label={isMobile ? 'Description *' : 'Scene Description *'}
                        value={sceneForm.sceneDescription}
                        onChange={(e) => setSceneForm({ ...sceneForm, sceneDescription: e.target.value })}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        placeholder="Describe what happens in this scene..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={currentSession.characters.map(c => c.name)}
                        value={sceneForm.charactersInScene}
                        onChange={(_, newValue) => setSceneForm({ ...sceneForm, charactersInScene: newValue })}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label={isMobile ? 'Characters' : 'Characters in Scene'} 
                            placeholder={isMobile ? 'Select...' : 'Select characters'}
                            InputProps={{
                              ...params.InputProps,
                              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                            }}
                            InputLabelProps={{
                              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1.5, sm: 2 }
                      }}>
                        <Button
                          variant="contained"
                          onClick={handleAddScene}
                          disabled={isLoading || !sceneForm.sceneDescription.trim()}
                          startIcon={isLoading ? <CircularProgress size={20} /> : (!isMobile && <AddIcon />)}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44
                          }}
                          fullWidth={isMobile}
                        >
                          {isMobile ? 'Add' : 'Add Scene'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setShowSceneDialog(false)}
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44
                          }}
                          fullWidth={isMobile}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={screenplayTab} index={3}>
            {/* Treatment Tab */}
            <Box>
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                Treatment
              </Typography>
              {currentSession.treatment ? (
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    '& p': { 
                      margin: '8px 0',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }, 
                    '& h1, & h2, & h3, & h4, & h5, & h6': { 
                      margin: '12px 0', 
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    },
                    '& ul, & ol': { 
                      margin: '8px 0', 
                      paddingLeft: { xs: '16px', sm: '20px' }
                    },
                    '& li': { 
                      margin: '4px 0',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}>
                    <ReactMarkdown>{currentSession.treatment}</ReactMarkdown>
                  </Box>
                  <Box sx={{ 
                    mt: { xs: 2, sm: 3 }, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 2 }
                  }}>
                    <Button
                      size="small"
                      startIcon={!isMobile && <ContentCopyIcon />}
                      onClick={() => copyToClipboard(currentSession.treatment || '')}
                      sx={{ 
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        minHeight: 44
                      }}
                      fullWidth={isMobile}
                    >
                      Copy
                    </Button>
                    <Button
                      size="small"
                      startIcon={!isMobile && <DownloadIcon />}
                      onClick={exportSession}
                      sx={{ 
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        minHeight: 44
                      }}
                      fullWidth={isMobile}
                    >
                      Export
                    </Button>
                  </Box>
                </Paper>
              ) : (
                <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    No treatment generated yet
                  </Typography>
                </Paper>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={screenplayTab} index={4}>
            {/* Script Tab */}
            <Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                mb: { xs: 2, sm: 3 }
              }}>
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  Screenplay
                </Typography>
                {currentSession.scriptContent && (
                  <Button
                    variant="outlined"
                    startIcon={!isMobile && <DownloadIcon />}
                    onClick={exportSession}
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44
                    }}
                    fullWidth={isMobile}
                  >
                    {isMobile ? 'Export' : 'Export Script'}
                  </Button>
                )}
              </Box>
              
              {currentSession.scriptContent ? (
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    gap: { xs: 0.5, sm: 0 }
                  }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {isMobile ? 'Pages: ' : 'Estimated pages: '}{ScreenplayService.estimatePageCount(currentSession.scriptContent)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {isMobile ? 'Words: ' : 'Word count: '}{currentSession.scriptContent.split(/\s+/).length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'Courier, monospace',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      whiteSpace: 'pre-wrap',
                      bgcolor: 'background.paper',
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      overflowX: 'auto',
                      maxHeight: '70vh',
                      overflow: 'auto'
                    }}
                  >
                    {currentSession.scriptContent}
                  </Box>
                </Paper>
              ) : (
                <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                  <ScriptIcon sx={{ 
                    fontSize: { xs: 48, sm: 60 }, 
                    color: 'text.secondary', 
                    mb: 2 
                  }} />
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'No Script Yet' : 'No Script Generated Yet'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {isMobile ? 'Add characters and scenes to start' : 'Add characters and scenes to generate your screenplay'}
                  </Typography>
                </Paper>
              )}
            </Box>
          </TabPanel>
        </TabPanel>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ 
            mt: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.8125rem', sm: '0.875rem' }
          }}
        >
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default ScreenplayGenerator;